import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface Props {
	targetId: string; // id of existing DOM node that the container will append to
	className?: string; // optional CSS class for the container
	children?: any; // renderable children of the Portal component that will be children of the container
}

// component for rendering children into a DOM node that exists outside the DOM hierarchy of the parent component
export default function Portal(props: Props) {
	const {
		targetId,
		className = '',
		children,
	} = props;

	const target = useRef<HTMLElement | null>(null);
	const container = useRef<HTMLDivElement>(document.createElement('div'));

	useEffect(() => {
		target.current = document.getElementById(targetId);

		if (target.current) {
			target.current.appendChild(container.current);
		}

		return () => {
			if (target.current && target.current.contains(container.current)) {
				target.current.removeChild(container.current);
			}
		};
	}, [targetId, container]);

	useEffect(() => {
		if (container.current.className !== className) {
			container.current.className = '';
			const classes = className.split(' ');
			container.current.classList.add(...classes);
		}
	}, [className]);

	return createPortal(children, container.current);
}